import { AllRoles, Role } from "@models/role";

/**
 * Route configuration constants
 * This is the source of truth for all route configurations
 * Use this in Storybook and other contexts where component imports should be avoided
 */

export const RootPath = "/app";
export const EntryPointPathRegex = new RegExp(`${RootPath}/([A-Za-z-])+$`);

// Check if the path is a the entry point of the nav bar options
export const isEntryPointPath = (path) => EntryPointPathRegex.test(path);

export const MultiOrgDashboardRouteConfig = {
  path: "/dashboard",
  exact: true,
  icon: "gauge",
  label: "Dashboard",
  roles: [Role.MULTI_ORG_VIEWER, Role.MULTI_ORG_ADMIN],
};

export const SingleOrgDashboardRouteConfig = {
  path: "/dashboard",
  exact: true,
  icon: "gauge",
  label: "Dashboard",
  roles: [Role.INSTRUCTOR, Role.ORG_ADMIN],
};

// Mirrors MultiOrgDashboardRouteConfig so the outcome-reporting feature gates
// the multi-org dashboard landing on the same roles as the legacy dashboard.
export const OutcomesMultiOrgRouteConfig = {
  path: "/dashboard",
  exact: true,
  icon: "gauge",
  label: "Dashboard",
  roles: [Role.MULTI_ORG_ADMIN, Role.MULTI_ORG_VIEWER],
};

export const OutcomesOrgDetailRouteConfig = {
  path: "/dashboard/org/:orgCode",
  exact: true,
  roles: [
    Role.MULTI_ORG_ADMIN,
    Role.MULTI_ORG_VIEWER,
    Role.ORG_ADMIN,
    Role.INSTRUCTOR,
  ],
};

export const OutcomesClassroomDetailRouteConfig = {
  path: "/dashboard/org/:orgCode/classroom/:classroomId",
  exact: true,
  roles: [
    Role.MULTI_ORG_ADMIN,
    Role.MULTI_ORG_VIEWER,
    Role.ORG_ADMIN,
    Role.INSTRUCTOR,
  ],
};

export const OutcomesLearnerDetailRouteConfig = {
  path: "/dashboard/org/:orgCode/classroom/:classroomId/learner/:learnerId",
  exact: true,
  roles: [
    Role.MULTI_ORG_ADMIN,
    Role.MULTI_ORG_VIEWER,
    Role.ORG_ADMIN,
    Role.INSTRUCTOR,
  ],
};

export const OrgsRouteConfig = {
  path: "/orgs",
  exact: false,
  icon: "sitemap",
  label: "Organizations",
  labelAbbr: "Orgs",
  roles: [Role.MULTI_ORG_VIEWER, Role.MULTI_ORG_ADMIN],
};

export const UsersRouteConfig = {
  path: "/users",
  exact: false,
  icon: "users",
  label: "Users",
  labelAbbr: "Users",
  roles: AllRoles,
};

export const ClassroomRouteConfig = {
  path: "/classrooms",
  exact: false,
  icon: "screen-users",
  label: "Classrooms",
  roles: AllRoles,
};

export const ProgramsRouteConfig = {
  path: "/programs",
  exact: false,
  icon: "layer-group",
  label: "Programs",
  roles: [Role.ORG_ADMIN, Role.MULTI_ORG_ADMIN, Role.MULTI_ORG_VIEWER],
};

export const ProfileRouteConfig = {
  path: "/profile",
  exact: true,
  roles: AllRoles,
};

export const SupportRouteConfig = {
  path: "https://support.transfrinc.com/",
  isExternalLink: true,
  icon: "question-circle",
  label: "Support Center",
  labelAbbr: "Support",
  roles: AllRoles,
};

export const CurricularRouteConfig = {
  path: "https://lookerstudio.google.com/reporting/d58f8625-ff07-4ef1-a687-1eb6ed3a43aa",
  isExternalLink: true,
  icon: "circle-nodes",
  label: "Curricular Mapping",
  labelAbbr: "Mapping",
  roles: AllRoles,
};

export const ProductCatalogRouteConfig = {
  isExternalLink: true,
  icon: "books",
  label: "Product Catalog",
  labelAbbr: "Catalog",
  roles: AllRoles,
};

export const MyWorkRouteConfig = {
  path: `/my-work`,
  exact: false,
  icon: "person-running",
  label: "My Work",
  roles: [Role.STUDENT],
  previewRoles: [Role.INSTRUCTOR, Role.ORG_ADMIN],
};

export const TrekRouteConfig = {
  path: `${process.env.CESE_FE_URL}/login`,
  isExternalLink: true,
  icon: "mountain",
  label: "Trek",
  roles: [Role.STUDENT],
};

export const DevicesRouteConfig = {
  path: "/devices",
  icon: "vr-cardboard",
  label: "Devices",
  labelAbbr: "Devices",
  roles: [Role.MULTI_ORG_ADMIN, Role.ORG_ADMIN, Role.MULTI_ORG_VIEWER],
};

export const IntegrationsRouteConfig = {
  path: "/integrations",
  exact: false,
  icon: "swap",
  label: "Integrations",
  labelAbbr: "Integrations",
  roles: AllRoles,
};

export const CurriculumResourcesRouteConfig = {
  path: "/curriculum-resources",
  exact: false,
  icon: "file-check",
  label: "Curriculum Resources",
  labelAbbr: "Curriculum",
  roles: AllRoles,
};

export const FundingRouteConfig = {
  path: "https://airtable.com/appmp66vdGVeBJktT/shrBA2nv31nWJn3Ku",
  isExternalLink: true,
  icon: "money-check-dollar-pen",
  label: "Funding Support",
  labelAbbr: "Funding",
  roles: AllRoles,
};

export const ActivityPlayerRouteConfig = {
  path: `/activity-player`,
  exact: false,
  icon: "person-running",
  label: "Activities",
  roles: [Role.STUDENT],
  previewRoles: [Role.INSTRUCTOR, Role.ORG_ADMIN],
};

// Aliases for backward compatibility (without "Config" suffix)
export {
  ActivityPlayerRouteConfig as ActivityPlayerRoute,
  ClassroomRouteConfig as ClassroomRoute,
  CurricularRouteConfig as CurricularRoute,
  CurriculumResourcesRouteConfig as CurriculumResourcesRoute,
  DevicesRouteConfig as DevicesRoute,
  FundingRouteConfig as FundingRoute,
  IntegrationsRouteConfig as IntegrationsRoute,
  MultiOrgDashboardRouteConfig as MultiOrgDashboardRoute,
  MyWorkRouteConfig as MyWorkRoute,
  OrgsRouteConfig as OrgsRoute,
  OutcomesClassroomDetailRouteConfig as OutcomesClassroomDetailRoute,
  OutcomesLearnerDetailRouteConfig as OutcomesLearnerDetailRoute,
  OutcomesMultiOrgRouteConfig as OutcomesMultiOrgRoute,
  OutcomesOrgDetailRouteConfig as OutcomesOrgDetailRoute,
  ProductCatalogRouteConfig as ProductCatalogRoute,
  ProfileRouteConfig as ProfileRoute,
  ProgramsRouteConfig as ProgramsRoute,
  SingleOrgDashboardRouteConfig as SingleOrgDashboardRoute,
  SupportRouteConfig as SupportRoute,
  TrekRouteConfig as TrekRoute,
  UsersRouteConfig as UsersRoute,
};
